/**
 * v5 InitiativePanel — combat turn order UI.
 *
 * Renders the current round, the ordered entry list with a current-turn
 * highlight, and (GM only) Prev/Next/Clear/Add controls. Players see
 * the panel read-only. Add modal lets the GM check tokens, set or roll
 * initiative values, and confirm.
 *
 * Roll button enablement (spec iter-4): only enabled when the token has
 * `characterId` set AND has a linked character (its initiative modifier).
 */

import { useMemo, useState } from 'react'
import { Button } from '@arcanecircle/ui/grimoire/button'
import { IconButton } from '@arcanecircle/ui/grimoire/icon-button'
import { Input } from '@arcanecircle/ui/ui/input'
import { Checkbox } from '@arcanecircle/ui/ui/checkbox'
import { useVTTStore } from '@/store'
import { useInitiativeStore, InitiativeEntry } from '@/store/initiativeStore'
import { socketService } from '@/services/socket'
import type { VTTCharacterData } from '@/services/characterService'
import type { Token } from '@/types'

interface InitiativePanelProps {
  isGM: boolean
  getCharacter?: (characterId: string) => VTTCharacterData | null
}

/**
 * Pull the initiative modifier off a character record. Uses the sheet's
 * `stats.initiative` so custom/derived modifiers are respected — and so the
 * Add-from-map roll matches the character-sheet Init button. Null only when
 * there's no linked character.
 */
function getInitMod(character: VTTCharacterData | null): number | null {
  if (!character) return null
  return character.stats.initiative
}

function rollD20(): number {
  return Math.floor(Math.random() * 20) + 1
}

export function InitiativePanel({ isGM, getCharacter }: InitiativePanelProps) {
  const { mapId, entries, currentIndex, round, error, setError } =
    useInitiativeStore()
  const { getActiveMap, getActiveMapTokens } = useVTTStore()
  const [showAdd, setShowAdd] = useState(false)

  const activeMap = getActiveMap()
  // Initiative is "started" for the active map once a row exists (store mapId
  // matches), even if its entry list is still empty (GM started, waiting for
  // players to roll in from their sheets — issue #162).
  const started = !!activeMap && mapId === activeMap.id

  const handleAdvance = () => {
    if (!activeMap) return
    socketService.emitInitiativeAdvance(activeMap.id)
  }
  const handleBack = () => {
    if (!activeMap) return
    socketService.emitInitiativeBack(activeMap.id)
  }
  const handleClear = () => {
    if (!activeMap) return
    if (!window.confirm('Clear initiative? This ends the encounter.')) return
    socketService.emitInitiativeClear(activeMap.id)
  }
  const handleSet = (newEntries: InitiativeEntry[]) => {
    if (!activeMap) return
    socketService.emitInitiativeSet(activeMap.id, newEntries)
    setShowAdd(false)
  }
  const handleRemove = (tokenId: string) => {
    if (!activeMap) return
    const next = entries.filter((e) => e.tokenId !== tokenId)
    socketService.emitInitiativeSet(activeMap.id, next)
  }

  return (
    <>
      <section className="ac-panel">
        <div className="ac-panel-header">
          <div>
            <div className="ac-panel-eyebrow">Combat · in session</div>
            <div className="ac-panel-title">
              Round {round}
              {entries[currentIndex] && (
                <span
                  style={{
                    fontSize: '0.6875rem',
                    color: 'hsl(var(--ac-mint-primary))',
                    fontFamily: 'var(--ac-font-sans)',
                    marginLeft: '0.5rem',
                  }}
                >
                  · {entries[currentIndex].name}'s turn
                </span>
              )}
            </div>
          </div>
          {isGM && entries.length > 0 && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleClear}
            >
              Clear
            </Button>
          )}
        </div>

        {error && (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              marginBottom: '0.5rem',
              fontSize: '0.75rem',
              color: 'hsl(0, 0%, 95%)',
              backgroundColor: 'hsl(0, 60%, 30%)',
              border: '1px solid hsl(0, 60%, 45%)',
              borderRadius: '0.375rem',
            }}
          >
            <span>{error}</span>
            <IconButton
              size="sm"
              variant="ghost"
              className="!w-[22px] !h-[22px] !border-transparent !bg-transparent !text-[hsl(0,0%,90%)]"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              ×
            </IconButton>
          </div>
        )}

        {entries.length === 0 ? (
          <div
            className="ac-help"
            style={{ textAlign: 'center', padding: '0.5rem' }}
          >
            {started ? (
              // Row exists but no combatants yet: GM started, players can now
              // roll in from their character sheets.
              isGM
                ? 'Initiative started. Add combatants below, or have players roll in from their sheets.'
                : 'Initiative started. Roll in from your character sheet.'
            ) : isGM ? (
              <>
                <div style={{ marginBottom: '0.5rem' }}>
                  No initiative yet. Start it, then players can roll in from
                  their sheets.
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setShowAdd(true)}
                  disabled={!activeMap}
                >
                  Start initiative
                </Button>
              </>
            ) : (
              'No active combat.'
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {entries.map((entry, idx) => {
              const isCurrent = idx === currentIndex
              const isPast = idx < currentIndex
              return (
                <div
                  key={entry.tokenId}
                  className="ac-card-flat"
                  style={{
                    padding: '0.5rem 0.625rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.625rem',
                    background: isCurrent
                      ? 'hsl(var(--ac-mint-primary) / 0.1)'
                      : undefined,
                    border: isCurrent
                      ? '1px solid hsl(var(--ac-mint-primary) / 0.4)'
                      : '1px solid transparent',
                    boxShadow: isCurrent ? 'var(--ac-inner-top)' : undefined,
                    opacity: isPast ? 0.5 : 1,
                  }}
                >
                  <span
                    className="font-mono-ac"
                    style={{
                      width: 20,
                      textAlign: 'right',
                      fontSize: '0.625rem',
                      color: isCurrent
                        ? 'hsl(var(--ac-mint-primary))'
                        : 'hsl(var(--ac-text-muted))',
                    }}
                  >
                    {idx + 1}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: '0.8125rem',
                      fontWeight: isCurrent ? 600 : 500,
                      color: isCurrent
                        ? 'hsl(var(--ac-mint-primary))'
                        : 'hsl(var(--ac-text-light))',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {entry.name}
                    {isCurrent && (
                      <span
                        style={{
                          marginLeft: '0.5rem',
                          fontSize: '0.625rem',
                          fontWeight: 500,
                        }}
                      >
                        ● now acting
                      </span>
                    )}
                  </span>
                  <span
                    className="font-serif-ac"
                    style={{
                      fontSize: '1rem',
                      color: isCurrent
                        ? 'hsl(var(--ac-mint-primary))'
                        : 'hsl(var(--ac-text-light))',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {entry.value}
                  </span>
                  {isGM && (
                    <IconButton
                      size="sm"
                      variant="ghost"
                      className="!w-[22px] !h-[22px] !text-[hsl(var(--ac-danger))]"
                      onClick={() => handleRemove(entry.tokenId)}
                      title="Remove from initiative"
                      aria-label={`Remove ${entry.name}`}
                    >
                      ×
                    </IconButton>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* GM controls */}
        {isGM && (
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              paddingTop: '0.5rem',
              borderTop: '1px solid hsl(var(--ac-dark-accent))',
            }}
          >
            <Button
              type="button"
              variant="outline"
              size="sm"
              style={{ flex: 1 }}
              onClick={handleBack}
              disabled={entries.length === 0}
              icon={
                <svg
                  width={11}
                  height={11}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              }
            >
              Prev
            </Button>
            <Button
              type="button"
              size="sm"
              style={{ flex: 1 }}
              onClick={handleAdvance}
              disabled={entries.length === 0}
              trailing={
                <svg
                  width={11}
                  height={11}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              }
            >
              Next
            </Button>
            <Button
              type="button"
              size="sm"
              style={{ flex: 2 }}
              onClick={() => setShowAdd(true)}
              disabled={!activeMap}
              icon={
                <svg
                  width={11}
                  height={11}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              }
            >
              Add from map
            </Button>
          </div>
        )}
      </section>

      {showAdd && activeMap && (
        <AddFromMapModal
          tokens={getActiveMapTokens()}
          existing={entries}
          getCharacter={getCharacter}
          onCancel={() => setShowAdd(false)}
          onConfirm={handleSet}
        />
      )}
    </>
  )
}

interface AddFromMapModalProps {
  tokens: Token[]
  existing: InitiativeEntry[]
  getCharacter?: (characterId: string) => VTTCharacterData | null
  onCancel: () => void
  onConfirm: (entries: InitiativeEntry[]) => void
}

function AddFromMapModal({
  tokens,
  existing,
  getCharacter,
  onCancel,
  onConfirm,
}: AddFromMapModalProps) {
  const initialDraft = useMemo(() => {
    const existingByToken = new Map(existing.map((e) => [e.tokenId, e]))
    return tokens.map((t) => {
      const ex = existingByToken.get(t.id)
      return {
        tokenId: t.id,
        name: t.name,
        value: ex?.value ?? 0,
        included: !!ex,
      }
    })
  }, [tokens, existing])

  const [draft, setDraft] = useState(initialDraft)

  const updateRow = (
    tokenId: string,
    patch: Partial<(typeof draft)[number]>,
  ) => {
    setDraft((prev) =>
      prev.map((r) => (r.tokenId === tokenId ? { ...r, ...patch } : r)),
    )
  }

  const handleRoll = (tokenId: string) => {
    const token = tokens.find((t) => t.id === tokenId)
    if (!token?.characterId || !getCharacter) return
    const character = getCharacter(token.characterId)
    const initMod = getInitMod(character)
    if (initMod === null) return
    const total = rollD20() + initMod
    updateRow(tokenId, { value: total, included: true })
  }

  const handleConfirm = () => {
    const entries: InitiativeEntry[] = draft
      .filter((r) => r.included)
      .map((r) => ({
        tokenId: r.tokenId,
        name: r.name,
        value: Number.isFinite(r.value) ? Math.round(r.value) : 0,
      }))
    onConfirm(entries)
  }

  return (
    <div
      role="dialog"
      aria-label="Add tokens to initiative"
      className="vtt-modal-backdrop"
      onClick={onCancel}
    >
      <div
        className="vtt-modal-surface"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 540 }}
      >
        <div className="vtt-modal-header">
          <div>
            <div className="ac-panel-eyebrow">Initiative</div>
            <div className="ac-panel-title">Add tokens</div>
          </div>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Close"
            onClick={onCancel}
          >
            <svg
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </IconButton>
        </div>

        <div className="vtt-modal-body">
          {draft.length === 0 ? (
            <div
              className="ac-help"
              style={{ textAlign: 'center', padding: '0.5rem' }}
            >
              No tokens on the active map.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {draft.map((row) => {
                const token = tokens.find((t) => t.id === row.tokenId)
                const character =
                  token?.characterId && getCharacter
                    ? getCharacter(token.characterId)
                    : null
                const initMod = getInitMod(character)
                const canRoll = !!token?.characterId && initMod !== null
                const rollTitle = canRoll
                  ? `Roll d20 ${initMod! >= 0 ? '+' : ''}${initMod} (Init)`
                  : 'No linked character.'

                return (
                  <div
                    key={row.tokenId}
                    className="ac-card-flat"
                    style={{
                      padding: '0.5rem 0.625rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.625rem',
                    }}
                  >
                    <Checkbox
                      checked={row.included}
                      onCheckedChange={(checked) =>
                        updateRow(row.tokenId, { included: checked })
                      }
                    />
                    {token && (
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: token.color,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <span
                      style={{
                        flex: 1,
                        fontSize: '0.8125rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.name}
                    </span>
                    <Input
                      type="number"
                      value={row.value}
                      min={-100}
                      max={100}
                      onChange={(e) =>
                        updateRow(row.tokenId, {
                          value: parseInt(e.target.value || '0', 10),
                          included: true,
                        })
                      }
                      style={{
                        width: 70,
                        height: 32,
                        fontSize: '0.8125rem',
                        padding: '0 0.5rem',
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant={canRoll ? 'primary' : 'outline'}
                      onClick={() => handleRoll(row.tokenId)}
                      disabled={!canRoll}
                      title={rollTitle}
                    >
                      Roll
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="vtt-modal-footer">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={handleConfirm}>
            Confirm
          </Button>
        </div>
      </div>
    </div>
  )
}
